import { ResourceFieldDataFragment } from 'generated/resources/graphql'

import users from 'modules/core/store/collections/users'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employeeGroups from 'modules/employees/store/collections/employeeGroups'
import type Employee from 'modules/employees/store/models/Employee'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import { isEmployeeLedByCurrent } from 'modules/teams/store/isEmployeeLedByCurrent'

// FIXME: Is this frontend or backend responsability
const OWNED = 'owned'
const EVERYBODY = 'everybody'
const REPORTEES = 'reportees'
const TEAM_LEADER = 'team_leader'
const LEGAL_ENTITY = 'legal_entity'

type PermissionValidator = {
  permissions: string[]
  contractVersions: ContractVersions
  employee?: Employee | null
}
const hasPermissions = ({
  permissions,
  contractVersions,
  employee,
}: PermissionValidator): boolean => {
  if (permissions.includes(EVERYBODY)) {
    return true
  }

  const currentEmployee = users.current.employee
  if (!employee || !currentEmployee) {
    return false
  }

  const isCurrentEmployee = currentEmployee === employee
  if (permissions.includes(OWNED) && isCurrentEmployee) {
    return true
  }
  if (permissions.includes(REPORTEES) && employee.isManagedByCurrent()) {
    return true
  }

  if (
    permissions.includes(TEAM_LEADER) &&
    isEmployeeLedByCurrent(teams, memberships, currentEmployee.id, employee, contractVersions)
  ) {
    return true
  }

  const currentEmployeeLegalEntityId = employeeGroups.get(
    currentEmployee.attributes.employee_group_id
  )?.attributes.legal_entity_id
  const employeeLegalEntityId = employeeGroups.get(employee.attributes.employee_group_id)
    ?.attributes.legal_entity_id

  const sameLegalEntity =
    currentEmployeeLegalEntityId &&
    employeeLegalEntityId &&
    currentEmployeeLegalEntityId === employeeLegalEntityId

  if (permissions.includes(LEGAL_ENTITY) && sameLegalEntity) {
    return true
  }

  return false
}

/**
 * @deprecated Use GraphQL's editable field from CustomFieldsResourceField
 */
export const isEditable = (
  editableField: string[],
  contractVersions: ContractVersions,
  employee?: Employee
) => {
  return hasPermissions({
    permissions: editableField,
    contractVersions,
    employee,
  })
}

/**
 * @deprecated Use GraphQL's editable field from CustomFieldsResourceField
 */
export const isVisible =
  (employee: Employee | null | undefined, contractVersions: ContractVersions) =>
  (visible: string[]) => {
    return hasPermissions({
      permissions: visible,
      contractVersions,
      employee,
    })
  }

// TODO: use GraphQL enum when entity is updated
type UsageGroupEnum =
  | 'employees-questions'
  | 'employee-profile'
  | 'basic-information'
  | 'identification-number'
  | 'general-information'

export const filterCustomResourceFieldsDataByUsageGroup = (
  customResourceFieldsData: ResourceFieldDataFragment[],
  usageGroup: UsageGroupEnum
) => {
  return customResourceFieldsData.filter(
    (customResourceField) => customResourceField.field?.usageGroup.slug === usageGroup
  )
}
